import { values } from 'lodash';
import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import {
  initiativeChangeInsightEmployees,
  initiativeChangeInsightGroups,
  initiativeChangeInsightOrganizationStakeholders,
  initiativeParticipationSchema,
  initiativeRelationsSchema,
  initiativeStakeholder,
  stakeholderParticipationSchema,
  surveyResponseSchema,
  surveySchema,
  surveySettingSchema,
  templatesResponseSchema,
} from '../lib/schema.js';
import { selectCurrentOrganizationId } from './auth.js';
import { deleteEntities, selectEntities } from './entities.js';

const RETRIEVE_TEMPLATES_REQUEST =
  'changesync/changeInsights/RETRIEVE_TEMPLATES_REQUEST';
const RETRIEVE_TEMPLATES_SUCCESS =
  'changesync/changeInsights/RETRIEVE_TEMPLATES_SUCCESS';
const RETRIEVE_TEMPLATES_FAILURE =
  'changesync/changeInsights/RETRIEVE_TEMPLATES_FAILURE';

const RETRIEVE_INITIATIVE_RELATIONS_REQUEST =
  'changesync/changeInsights/RETRIEVE_INITIATIVE_RELATIONS_REQUEST';
const RETRIEVE_INITIATIVE_RELATIONS_SUCCESS =
  'changesync/changeInsights/RETRIEVE_INITIATIVE_RELATIONS_SUCCESS';
const RETRIEVE_INITIATIVE_RELATIONS_FAILURE =
  'changesync/changeInsights/RETRIEVE_INITIATIVE_RELATIONS_FAILURE';

const RETRIEVE_CHANGE_INSIGHT_REQUEST =
  'changesync/changeInsights/RETRIEVE_CHANGE_INSIGHT_REQUEST';
const RETRIEVE_CHANGE_INSIGHT_SUCCESS =
  'changesync/changeInsights/RETRIEVE_CHANGE_INSIGHT_SUCCESS';
const RETRIEVE_CHANGE_INSIGHT_FAILURE =
  'changesync/changeInsights/RETRIEVE_CHANGE_INSIGHT_FAILURE';

const DUPLICATE_CHANGE_INSIGHT_REQUEST =
  'changesync/changeInsights/DUPLICATE_CHANGE_INSIGHT_REQUEST';
const DUPLICATE_CHANGE_INSIGHT_SUCCESS =
  'changesync/changeInsights/DUPLICATE_CHANGE_INSIGHT_SUCCESS';
const DUPLICATE_CHANGE_INSIGHT_FAILURE =
  'changesync/changeInsights/DUPLICATE_CHANGE_INSIGHT_FAILURE';

const RETRIEVE_SURVEY_REQUEST =
  'changesync/changeInsights/RETRIEVE_SURVEY_REQUEST';
const RETRIEVE_SURVEY_SUCCESS =
  'changesync/changeInsights/RETRIEVE_SURVEY_SUCCESS';
const RETRIEVE_SURVEY_FAILURE =
  'changesync/changeInsights/RETRIEVE_SURVEY_FAILURE';

const CREATE_SURVEY_REQUEST = 'changesync/changeInsights/CREATE_SURVEY_REQUEST';
const CREATE_SURVEY_SUCCESS = 'changesync/changeInsights/CREATE_SURVEY_SUCCESS';
const CREATE_SURVEY_FAILURE = 'changesync/changeInsights/CREATE_SURVEY_FAILURE';

const APPLY_SURVEY_REQUEST = 'changesync/changeInsights/APPLY_SURVEY_REQUEST';
const APPLY_SURVEY_SUCCESS = 'changesync/changeInsights/APPLY_SURVEY_SUCCESS';
const APPLY_SURVEY_FAILURE = 'changesync/changeInsights/APPLY_SURVEY_FAILURE';

const UPDATE_SURVEY_REQUEST = 'changesync/changeInsights/UPDATE_SURVEY_REQUEST';
const UPDATE_SURVEY_SUCCESS = 'changesync/changeInsights/UPDATE_SURVEY_SUCCESS';
const UPDATE_SURVEY_FAILURE = 'changesync/changeInsights/UPDATE_SURVEY_FAILURE';

const UPDATE_CHANGE_INSIGHTS_DETAILS_REQUEST =
  'changesync/changeInsights/UPDATE_CHANGE_INSIGHTS_DETAILS_REQUEST';
const UPDATE_CHANGE_INSIGHTS_DETAILS_SUCCESS =
  'changesync/changeInsights/UPDATE_CHANGE_INSIGHTS_DETAILS_SUCCESS';
const UPDATE_CHANGE_INSIGHTS_DETAILS_FAILURE =
  'changesync/changeInsights/UPDATE_CHANGE_INSIGHTS_DETAILS_FAILURE';

const UPDATE_COMPANY_SETTING_REQUEST =
  'changesync/changeInsights/UPDATE_COMPANY_SETTING_REQUEST';
const UPDATE_COMPANY_SETTING_SUCCESS =
  'changesync/changeInsights/UPDATE_COMPANY_SETTING_SUCCESS';
const UPDATE_COMPANY_SETTING_FAILURE =
  'changesync/changeInsights/UPDATE_COMPANY_SETTING_FAILURE';

const SEND_SURVEY_REQUEST = 'changesync/changeInsights/SEND_SURVEY_REQUEST';
const SEND_SURVEY_SUCCESS = 'changesync/changeInsights/SEND_SURVEY_SUCCESS';
const SEND_SURVEY_FAILURE = 'changesync/changeInsights/SEND_SURVEY_FAILURE';

const SHARE_SURVEY_LINK_REQUEST =
  'changesync/changeInsights/SHARE_SURVEY_LINK_REQUEST';
const SHARE_SURVEY_LINK_SUCCESS =
  'changesync/changeInsights/SHARE_SURVEY_LINK_SUCCESS';
const SHARE_SURVEY_LINK_FAILURE =
  'changesync/changeInsights/SHARE_SURVEY_LINK_FAILURE';

const DELETE_SURVEY_REQUEST = 'changesync/changeInsights/DELETE_SURVEY_REQUEST';
const DELETE_SURVEY_SUCCESS = 'changesync/changeInsights/DELETE_SURVEY_SUCCESS';
const DELETE_SURVEY_FAILURE = 'changesync/changeInsights/DELETE_SURVEY_FAILURE';

const DELETE_CHANGE_INSIGHTS_REQUEST =
  'changesync/changeInsights/DELETE_CHANGE_INSIGHTS_REQUEST';
const DELETE_CHANGE_INSIGHTS_SUCCESS =
  'changesync/changeInsights/DELETE_CHANGE_INSIGHTS_SUCCESS';
const DELETE_CHANGE_INSIGHTS_FAILURE =
  'changesync/changeInsights/DELETE_CHANGE_INSIGHTS_FAILURE';

const initialState = {
  retrieveTemplates: {
    templateIds: null,
    pending: false,
    success: false,
    error: null,
  },
  retrieveInitiativeRelations: {
    detailIds: {
      employees: [],
      groups: [],
      organizationStakeholders: [],
    },
    pending: false,
    success: false,
    error: null,
  },
  retrieveChangeInsightDetails: {
    detailIds: {
      survey: null,
      initiativeParticipations: [],
      stakeholderParticipations: [],
      ancestry: [],
      surveyResponses: [],
    },
    pending: false,
    success: false,
    error: null,
  },
};

// Reducer
export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_CHANGE_INSIGHT_REQUEST:
      return {
        ...state,
        retrieveChangeInsightDetails: {
          ...state.retrieveChangeInsightDetails,
          pending: true,
          success: false,
          error: null,
        },
      };

    case RETRIEVE_CHANGE_INSIGHT_SUCCESS:
      return {
        ...state,
        retrieveChangeInsightDetails: {
          detailIds: action.payload.result,
          pending: false,
          success: true,
        },
      };

    case RETRIEVE_CHANGE_INSIGHT_FAILURE:
      return {
        ...state,
        retrieveChangeInsightDetails: {
          ...state.retrieveChangeInsightDetails,
          pending: false,
          error: action.error,
        },
      };

    case RETRIEVE_INITIATIVE_RELATIONS_REQUEST:
      return {
        ...state,
        retrieveInitiativeRelations: {
          ...state.retrieveInitiativeRelations,
          pending: true,
          success: false,
          error: null,
        },
      };

    case RETRIEVE_INITIATIVE_RELATIONS_SUCCESS:
      return {
        ...state,
        retrieveInitiativeRelations: {
          detailIds: action.payload.result,
          pending: false,
          success: true,
        },
      };

    case RETRIEVE_INITIATIVE_RELATIONS_FAILURE:
      return {
        ...state,
        retrieveInitiativeRelations: {
          ...state.retrieveInitiativeRelations,
          pending: false,
          error: action.error,
        },
      };

    case RETRIEVE_TEMPLATES_REQUEST:
      return {
        ...state,
        retrieveTemplates: {
          ...state.retrieveTemplates,
          pending: true,
          success: false,
          error: null,
        },
      };

    case RETRIEVE_TEMPLATES_SUCCESS:
      return {
        ...state,
        retrieveTemplates: {
          templateIds: action.payload.result,
          pending: false,
          success: true,
        },
      };

    case RETRIEVE_TEMPLATES_FAILURE:
      return {
        ...state,
        retrieveTemplates: {
          ...state.retrieveTemplates,
          pending: false,
          error: action.error,
        },
      };

    case DUPLICATE_CHANGE_INSIGHT_SUCCESS:
      return {
        ...state,
        retrieveTemplates: {
          templateIds: [
            ...state.retrieveTemplates.templateIds,
            action.payload.id,
          ],
          pending: false,
          success: true,
        },
      };

    case DELETE_CHANGE_INSIGHTS_SUCCESS:
      return {
        ...state,
        retrieveChangeInsightDetails: {
          detailIds: {
            survey: null,
            initiativeParticipations: [],
            stakeholderParticipations: [],
            ancestry: [],
            surveyResponses: [],
          },
          pending: false,
          success: false,
          error: null,
        },
      };
    default:
      return state;
  }
};
// Action Creators

export const retrieveTemplatesFeedbackForm = () => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());
  const endpoint = `organizations/${organizationId}/survey_templates`;

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_TEMPLATES_REQUEST,
        RETRIEVE_TEMPLATES_SUCCESS,
        RETRIEVE_TEMPLATES_FAILURE,
      ],
      endpoint,
      method: HTTP_METHODS.GET,
      schema: [templatesResponseSchema],
    },
  });
};

export const retrieveChangeInsightDetails =
  (surveyId, query, isDelay) => (dispatch, getState) => {
    const endpoint = `surveys/${surveyId}/change_insights`;

    return dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_CHANGE_INSIGHT_REQUEST,
          RETRIEVE_CHANGE_INSIGHT_SUCCESS,
          RETRIEVE_CHANGE_INSIGHT_FAILURE,
        ],
        endpoint,
        method: HTTP_METHODS.GET,
        schema: surveySchema,
        query,
      },
      meta: isDelay
        ? {
            query,
            debounce: {
              time: 500,
              key: 'CHANGE_INSIGHTS',
            },
          }
        : {},
    });
  };

export const retrieveChangeInsight = (templateId, token) => (dispatch) => {
  const endpoint = `surveys/${templateId}`;
  const query = token ? `?token=${token}` : '';

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_SURVEY_REQUEST,
        RETRIEVE_SURVEY_SUCCESS,
        RETRIEVE_SURVEY_FAILURE,
      ],
      endpoint: endpoint + query,
      method: HTTP_METHODS.GET,
    },
  });
};

export const retrieveInitiativeRelations =
  (initiativeType, initiativeId) => (dispatch, getState) => {
    const endpoint = `${initiativeType}_initiatives/${initiativeId}/initiative_relations`;

    return dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_INITIATIVE_RELATIONS_REQUEST,
          RETRIEVE_INITIATIVE_RELATIONS_SUCCESS,
          RETRIEVE_INITIATIVE_RELATIONS_FAILURE,
        ],
        endpoint,
        schema: initiativeRelationsSchema,
        method: HTTP_METHODS.GET,
      },
    });
  };

export const sendChangeInsightSurvey =
  (surveyId, emailSettings) => (dispatch, getState) => {
    const endpoint = `surveys/${surveyId}/send_survey`;

    return dispatch({
      [CALL_API]: {
        types: [SEND_SURVEY_REQUEST, SEND_SURVEY_SUCCESS, SEND_SURVEY_FAILURE],
        endpoint,
        method: HTTP_METHODS.POST,
        body: emailSettings,
      },
    });
  };

export const sendResponseSurvey =
  (surveyResponse, token) => (dispatch, getState) => {
    const endpoint = 'survey_responses';
    const query = token ? `?token=${token}` : '';

    return dispatch({
      [CALL_API]: {
        types: [SEND_SURVEY_REQUEST, SEND_SURVEY_SUCCESS, SEND_SURVEY_FAILURE],
        endpoint: endpoint + query,
        body: surveyResponse,
        method: HTTP_METHODS.POST,
      },
    });
  };

export const createChangeInsight = (changeInsight) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        CREATE_SURVEY_REQUEST,
        CREATE_SURVEY_SUCCESS,
        CREATE_SURVEY_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/survey_templates`,
      method: HTTP_METHODS.POST,
      body: { ...changeInsight },
      schema: templatesResponseSchema,
    },
  });
};

export const updateChangeInsightDetails =
  (surveyId, changeInsight) => (dispatch, getState) => {
    return dispatch({
      [CALL_API]: {
        types: [
          UPDATE_CHANGE_INSIGHTS_DETAILS_REQUEST,
          UPDATE_CHANGE_INSIGHTS_DETAILS_SUCCESS,
          UPDATE_CHANGE_INSIGHTS_DETAILS_FAILURE,
        ],
        endpoint: `surveys/${surveyId}`,
        method: HTTP_METHODS.PUT,
        body: { ...changeInsight },
      },
    });
  };

export const updateChangeInsightSettings =
  (changeInsight) => (dispatch, getState) => {
    const organizationId = selectCurrentOrganizationId(getState());

    const endpoint = `organizations/${organizationId}`;
    return dispatch({
      [CALL_API]: {
        types: [
          UPDATE_COMPANY_SETTING_REQUEST,
          UPDATE_COMPANY_SETTING_SUCCESS,
          UPDATE_COMPANY_SETTING_FAILURE,
        ],
        endpoint,
        method: HTTP_METHODS.PUT,
        body: { ...changeInsight },
      },
    });
  };

export const applyChangeInsight =
  (initiativeId, initiativeType, changeInsight) => (dispatch, getState) => {
    return dispatch({
      [CALL_API]: {
        types: [
          APPLY_SURVEY_REQUEST,
          APPLY_SURVEY_SUCCESS,
          APPLY_SURVEY_FAILURE,
        ],
        endpoint: `${initiativeType}_initiatives/${initiativeId}/surveys/create`,
        method: HTTP_METHODS.POST,
        body: { ...changeInsight },
      },
    });
  };

export const authorizeShareLink =
  ({ email, token }) =>
  (dispatch) => {
    return dispatch({
      [CALL_API]: {
        types: [
          SHARE_SURVEY_LINK_REQUEST,
          SHARE_SURVEY_LINK_SUCCESS,
          SHARE_SURVEY_LINK_FAILURE,
        ],
        endpoint: 'authorize_survey_by_token',
        method: HTTP_METHODS.POST,
        body: { email, token },
      },
    });
  };

export const onShareLink = (id) => (dispatch) => {
  return dispatch({
    [CALL_API]: {
      types: [
        SHARE_SURVEY_LINK_REQUEST,
        SHARE_SURVEY_LINK_SUCCESS,
        SHARE_SURVEY_LINK_FAILURE,
      ],
      endpoint: `surveys/${id}/share_link`,
      method: HTTP_METHODS.POST,
    },
  });
};

export const updateChangeInsight = (templateId, template) => (dispatch) =>
  dispatch({
    [CALL_API]: {
      types: [
        UPDATE_SURVEY_REQUEST,
        UPDATE_SURVEY_SUCCESS,
        UPDATE_SURVEY_FAILURE,
      ],
      endpoint: `survey_templates/${templateId}`,
      method: HTTP_METHODS.PUT,
      body: { ...template },
    },
  });

export const deleteChangeInsightTemplate = (id) => async (dispatch) => {
  await dispatch({
    [CALL_API]: {
      types: [
        DELETE_SURVEY_REQUEST,
        DELETE_SURVEY_SUCCESS,
        DELETE_SURVEY_FAILURE,
      ],
      endpoint: `survey_templates/${id}`,
      method: HTTP_METHODS.DELETE,
    },
  });

  return dispatch(deleteEntities([`templates.${id}`]));
};

export const deleteSurveyFromInitiative = (id) => async (dispatch) => {
  await dispatch({
    [CALL_API]: {
      types: [
        DELETE_CHANGE_INSIGHTS_REQUEST,
        DELETE_CHANGE_INSIGHTS_SUCCESS,
        DELETE_CHANGE_INSIGHTS_FAILURE,
      ],
      endpoint: `surveys/${id}`,
      method: HTTP_METHODS.DELETE,
    },
  });
};

export const duplicateChangeInsightTemplate = (template) => {
  return {
    type: DUPLICATE_CHANGE_INSIGHT_SUCCESS,
    payload: template,
  };
};

export const selectRetrieveTemplatesState = (state) =>
  state.changeInsights.retrieveTemplates;

export const selectTemplates = (state) => values(state.entities.templates);

export const selectChangeInsightSettings = (id) => (state) =>
  denormalize(id, surveySettingSchema, state.entities);

export const selectRetrieveChangInsightsState = (state) =>
  state.changeInsights.retrieveChangeInsightDetails;

const selectSurveyResponsesIds = createSelector(
  selectRetrieveChangInsightsState,
  (state) => state.detailIds.surveyResponses
);

const selectParticipationsIds = createSelector(
  selectRetrieveChangInsightsState,
  (state) => state.detailIds.initiativeParticipations
);

const selectStakeholdersIds = createSelector(
  selectRetrieveChangInsightsState,
  (state) => state.detailIds.stakeholderParticipations
);

export const selectSurveyResponsesResults = createSelector(
  selectSurveyResponsesIds,
  selectEntities,
  (surveyResponseIds, entities) =>
    denormalize(surveyResponseIds, [surveyResponseSchema], entities)
);

export const selectChangeInsightParticipationsResults = createSelector(
  selectParticipationsIds,
  selectEntities,
  (participationIds, entities) =>
    denormalize(participationIds, [initiativeParticipationSchema], entities)
);

export const selectChangeInsightStakeholdersResults = createSelector(
  selectStakeholdersIds,
  selectEntities,
  (stakeholderIds, entities) =>
    denormalize(stakeholderIds, [stakeholderParticipationSchema], entities)
);

export const selectRetrieveInitiativeRelationsState = (state) =>
  state.changeInsights.retrieveInitiativeRelations;

const selectChangeInsightEmployeesIds = createSelector(
  selectRetrieveInitiativeRelationsState,
  (state) => state.detailIds.employees
);
export const selectChangeInsightEmployeesResults = createSelector(
  selectChangeInsightEmployeesIds,
  selectEntities,
  (stakeholderIds, entities) =>
    denormalize(stakeholderIds, [initiativeChangeInsightEmployees], entities)
);

const selectChangeInsightGroupsIds = createSelector(
  selectRetrieveInitiativeRelationsState,
  (state) => state.detailIds.groups
);
export const selectChangeInsightGroupsResults = createSelector(
  selectChangeInsightGroupsIds,
  selectEntities,
  (stakeholderIds, entities) =>
    denormalize(stakeholderIds, [initiativeChangeInsightGroups], entities)
);

const selectChangeInsightOrganizationStakeholdersIds = createSelector(
  selectRetrieveInitiativeRelationsState,
  (state) => state.detailIds.organizationStakeholders
);
export const selectChangeInsightOrganizationStakeholdersResults =
  createSelector(
    selectChangeInsightOrganizationStakeholdersIds,
    selectEntities,
    (stakeholderIds, entities) =>
      denormalize(
        stakeholderIds,
        [initiativeChangeInsightOrganizationStakeholders],
        entities
      )
  );

export const selectInitiativeStakeholder = (id) =>
  createSelector(selectEntities, (entities) =>
    denormalize(id, initiativeStakeholder, entities)
  );
